import { useState } from "react";
import {
  Select,
  Space,
  Tooltip,
  Modal,
  Form,
  InputNumber,
  Typography,
  Button,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useCompanyProducts,
  type CompanyProduct,
} from "../../../hooks/useCompanyProducts";
import { useCompany } from "../../../hooks/useCompany";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { companyProductToBookingProduct } from "../utils/companyProductUtils";
import { companyProductMatchesSearch } from "../../../utils/companyProductSearch";
import type { BookingState } from "../../../contexts/BookingWizardContext";

type BookingProduct = NonNullable<BookingState["products"]>[number];

interface SavedProductPickerProps {
  products: BookingProduct[];
  onAdd: (product: BookingProduct) => void;
  /** Inline add row — hides secondary label */
  compact?: boolean;
}

export const SavedProductPicker = ({
  products,
  onAdd,
  compact = false,
}: SavedProductPickerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const { state } = useBookingWizard();
  const {
    products: companyProducts,
    deleteCompanyProduct,
  } = useCompanyProducts(selectedCompany);

  const [selectedProduct, setSelectedProduct] = useState<CompanyProduct | null>(
    null
  );
  const [cartonModalOpen, setCartonModalOpen] = useState(false);
  const [cartonForm] = Form.useForm<{ cartonCount: number }>();

  const handleSelect = (value: number | undefined) => {
    if (!value) {
      setSelectedProduct(null);
      return;
    }
    const product = companyProducts.find((p) => p.id === value);
    if (product) {
      setSelectedProduct(product);
      setCartonModalOpen(true);
    }
  };

  const handleCartonSubmit = async () => {
    if (!selectedProduct) return;
    try {
      const { cartonCount } = await cartonForm.validateFields();
      const bookingProduct = companyProductToBookingProduct(
        selectedProduct,
        cartonCount
      );

      const alreadyAdded = products.some(
        (p) =>
          (p as BookingProduct & { company_product_id?: number })
            .company_product_id === selectedProduct.id
      );
      if (alreadyAdded) {
        Modal.warning({
          title: t("bookingWizardV2.details.productAlreadyAdded"),
          content: t("bookingWizardV2.details.productAlreadyAddedHint"),
        });
        return;
      }

      onAdd(bookingProduct);
      setCartonModalOpen(false);
      cartonForm.resetFields();
      setSelectedProduct(null);
    } catch {
      // validation
    }
  };

  const handleDelete = async (
    product: CompanyProduct,
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation();
    Modal.confirm({
      title: t("bookingWizard.productDetails.deleteProduct"),
      content: product.name,
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: () => deleteCompanyProduct(product.id),
    });
  };

  const handleEdit = (
    product: CompanyProduct,
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation();
    navigate("/my-products", {
      state: {
        editProductId: product.id,
        returnToStep: 1,
        forceStep: 1,
        fromProductPage: true,
        bookingId: state.id,
        previousProducts: products,
        wizardVersion: "v2",
      },
    });
  };

  return (
    <>
      <Space
        direction="vertical"
        style={{ width: "100%", marginBottom: compact ? 8 : 16 }}
        size={compact ? 4 : 8}
      >
        {!compact && (
          <Typography.Text type="secondary">
            {t("bookingWizard.productDetails.selectFromSavedProducts")}
          </Typography.Text>
        )}
        <Select
          showSearch
          placeholder={t(
            compact
              ? "bookingWizardV2.details.addFromSavedProducts"
              : "bookingWizard.productDetails.searchSavedProductPlaceholder"
          )}
          style={{ width: "100%" }}
          allowClear
          onChange={handleSelect}
          value={undefined}
          optionFilterProp="value"
          filterOption={(input, option) => {
            const product = companyProducts.find((p) => p.id === option?.value);
            return product
              ? companyProductMatchesSearch(product, input)
              : false;
          }}
          notFoundContent={t("bookingWizard.productDetails.noSavedProducts")}
        >
          {companyProducts.map((product) => (
            <Select.Option key={product.id} value={product.id}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <strong>{product.name}</strong>
                  {product.ean != null && (
                    <span style={{ marginLeft: 8, color: "#8c8c8c" }}>
                      EAN: {product.ean}
                    </span>
                  )}
                </div>
                <Space onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={t("bookingWizard.productDetails.editProduct")}>
                    <EditOutlined
                      style={{ color: "#1890ff" }}
                      onClick={(e) => handleEdit(product, e)}
                    />
                  </Tooltip>
                  <Tooltip title={t("bookingWizard.productDetails.deleteProduct")}>
                    <DeleteOutlined
                      style={{ color: "#ff4d4f" }}
                      onClick={(e) => handleDelete(product, e)}
                    />
                  </Tooltip>
                </Space>
              </div>
            </Select.Option>
          ))}
        </Select>
        <Button
          type="link"
          icon={<PlusOutlined />}
          onClick={() =>
            navigate("/my-products", {
              state: {
                returnToStep: 1,
                forceStep: 1,
                fromProductPage: true,
                bookingId: state.id,
                previousProducts: products,
                wizardVersion: "v2",
              },
            })
          }
          style={{ padding: 0 }}
        >
          {t("bookingWizardV2.details.manageProducts")}
        </Button>
      </Space>

      <Modal
        title={t("bookingWizard.productDetails.cartonCountTitle")}
        open={cartonModalOpen}
        onOk={handleCartonSubmit}
        onCancel={() => {
          setCartonModalOpen(false);
          cartonForm.resetFields();
          setSelectedProduct(null);
        }}
        okText={t("common.add")}
      >
        <Form form={cartonForm} layout="vertical">
          <Form.Item
            name="cartonCount"
            label={t("bookingWizard.productDetails.cartonCountLabel")}
            rules={[
              {
                required: true,
                message: t("bookingWizard.productDetails.cartonCountRequired"),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              placeholder={t(
                "bookingWizard.productDetails.cartonCountPlaceholder"
              )}
            />
          </Form.Item>
          {selectedProduct && (
            <div style={{ marginTop: 8 }}>
              <Typography.Text type="secondary">
                {t("bookingWizard.productDetails.productDetailsPerCarton")}
              </Typography.Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {selectedProduct.pieces_per_carton != null && (
                  <li>
                    {t("bookingWizard.productDetails.piecesPerCarton")}:{" "}
                    {selectedProduct.pieces_per_carton}
                  </li>
                )}
                {selectedProduct.weight_per_carton != null && (
                  <li>
                    {t("bookingWizard.productDetails.weightPerCarton")}:{" "}
                    {selectedProduct.weight_per_carton} kg
                  </li>
                )}
              </ul>
            </div>
          )}
        </Form>
      </Modal>
    </>
  );
};
